import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { VENDOR_PRODUCT_OPTIONS, getBrandsForProduct } from "@/lib/productOptions";
import { digitsOnly, phoneInputProps } from "@/lib/numberInput";
import Navbar from "@/components/Navbar";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Analytics } from "@/lib/analyticsShim";

const RAILAXIS_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/assets/Logo/Railaxis_logo.png`;

const ROLE_OPTIONS = [
  { value: "contractor", label: "Contractor" },
  { value: "vendor", label: "Vendor" },
  { value: "admin", label: "Admin" },
];

const DOC_FIELDS = [
  { key: "gst_doc", label: "GST Document (Photo/PDF)" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("contractor");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
    phone: "",
    vendor_product_slug: VENDOR_PRODUCT_OPTIONS[0].slug,
    vendor_brand: getBrandsForProduct(VENDOR_PRODUCT_OPTIONS[0].slug)[0] || "",
  });
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isVendor = role === "vendor";
  const vendorBrandOptions = getBrandsForProduct(form.vendor_product_slug);

  const onFileChange = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file || null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (role === "admin") {
        const data = await register({
          role,
          name: form.name,
          company: form.company,
          email: form.email,
          password: form.password,
        });
        toast.success(data?.message || "Admin account created. Waiting for approval.");
        navigate("/login");
        return;
      }

      if (!/^\d{10}$/.test(form.phone)) {
        throw new Error("Phone number must be 10 digits");
      }

      const missing = DOC_FIELDS.filter((f) => !files[f.key]).map((f) => f.label);
      if (missing.length) {
        throw new Error(`Please upload all required documents. Missing: ${missing.join(", ")}`);
      }

      const fd = new FormData();
      fd.append("role", role);
      fd.append("name", form.name);
      fd.append("company", form.company);
      fd.append("phone", form.phone);
      if (form.email) fd.append("email", form.email);
      if (isVendor) {
        fd.append("vendor_product_slug", form.vendor_product_slug);
        fd.append("vendor_brand", form.vendor_brand);
      }

      DOC_FIELDS.forEach((field) => fd.append(field.key, files[field.key]));

      await register(fd, true);
      toast.success("Signup completed. Please login with OTP.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isAdmin = role === "admin";

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-2xl mx-auto px-6 py-16 md:py-20" data-testid="register-page">
        <div className="flex items-center gap-2 justify-center mb-6">
          <img
            src={RAILAXIS_LOGO_SRC}
            alt="RailAxis logo"
            className="w-14 h-14 rounded-md object-contain border border-gray-100 bg-white"
          />
        </div>
        <h1 className="font-display font-bold text-3xl text-[#111827] text-center tracking-tight">Create your account</h1>
        <p className="text-center text-[#4B5563] mt-2 text-sm">Select role and complete onboarding.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Signup As</label>
            <select
              value={role}
              onChange={(e) => {
                const nextRole = e.target.value;
                setRole(nextRole);
                if (nextRole === "vendor") {
                  const defaultProduct = VENDOR_PRODUCT_OPTIONS[0].slug;
                  const defaultBrand = getBrandsForProduct(defaultProduct)[0] || "";
                  setForm((prev) => ({
                    ...prev,
                    vendor_product_slug: defaultProduct,
                    vendor_brand: defaultBrand,
                  }));
                }
              }}
              className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">
              {isAdmin ? "Full Name" : "Customer Name"}
            </label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Company Name</label>
            <input
              required={!isAdmin}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
            />
          </div>

          {isAdmin ? (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Phone Number</label>
                <input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: digitsOnly(e.target.value, 10) })}
                  {...phoneInputProps}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                />
              </div>
              {isVendor && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Category</label>
                    <select
                      value={form.vendor_product_slug}
                      onChange={(e) => {
                        const selectedProduct = e.target.value;
                        const defaultBrand = getBrandsForProduct(selectedProduct)[0] || "";
                        setForm((prev) => ({
                          ...prev,
                          vendor_product_slug: selectedProduct,
                          vendor_brand: defaultBrand,
                        }));
                      }}
                      className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                    >
                      {VENDOR_PRODUCT_OPTIONS.map((option) => (
                        <option key={option.slug} value={option.slug}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Brand</label>
                    <select
                      value={form.vendor_brand}
                      onChange={(e) => setForm((prev) => ({ ...prev, vendor_brand: e.target.value }))}
                      className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                    >
                      {vendorBrandOptions.map((brand) => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">Email (Optional)</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                />
              </div>
              {DOC_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-[#111827] mb-1.5">{field.label}</label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    required
                    onChange={(e) => onFileChange(field.key, e.target.files?.[0])}
                    className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm gold-focus"
                  />
                </div>
              ))}
            </>
          )}

          {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full rounded-md py-3 font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="register-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isAdmin ? "Create Account" : "Submit KYC & Signup"}
          </button>

          <p className="text-sm text-center text-[#4B5563]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#C8A96A] font-semibold hover:underline" data-testid="to-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </section>
    </div>
  );
}
